import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { profileService } from '../../../services/userService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

interface Profile {
    id: number;
    name: string;
    description?: string;
    modules?: Array<{ id: number; name: string; code: string; is_active: boolean }>;
}

const ProfileListPage: React.FC = () => {
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<number | null>(null);
    const [pendingDelete, setPendingDelete] = useState<Profile | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await profileService.getAll({ per_page: 100, search: search || undefined });
            setProfiles((result.data ?? []) as Profile[]);
        } catch {
            setError('Error al cargar perfiles');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { load(); }, [load]);

    const requestDelete = (profile: Profile) => {
        setPendingDelete(profile);
    };

    const cancelDelete = () => {
        if (deleting !== null) return;
        setPendingDelete(null);
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        const profile = pendingDelete;
        setDeleting(profile.id);
        setPendingDelete(null);
        try {
            await profileService.delete(profile.id);
            setProfiles(prev => prev.filter(p => p.id !== profile.id));
        } catch {
            setError('Error al eliminar perfil');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/admin-dashboard' }, { label: 'Perfiles' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="card-title mb-0">
                                    <i className="fa fa-id-card me-2"></i>Perfiles de Acceso
                                </h4>
                                <Link to="/admin/profiles/new" className="btn btn-primary btn-sm">
                                    <i className="fa fa-plus me-1"></i> Nuevo Perfil
                                </Link>
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Buscar perfiles..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}

                            {loading ? (
                                <Spinner variant="full-page" text="Cargando..." size="md" />
                            ) : (
                                <div className="row g-3">
                                    {profiles.length === 0 ? (
                                        <EmptyState icon="fa-id-badge" title="No hay perfiles registrados" />
                                    ) : profiles.map(profile => (
                                        <div key={profile.id} className="col-md-6 col-lg-4">
                                            <div className="card h-100" style={{ border: '1px solid #e2e8f0' }}>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="card-title mb-0">
                                                            <i className="fa fa-id-badge me-2 text-primary"></i>
                                                            {profile.name}
                                                        </h6>
                                                        <span className="badge bg-info">
                                                            {(profile.modules ?? []).filter(m => m.is_active).length} módulos
                                                        </span>
                                                    </div>
                                                    {profile.description && (
                                                        <p className="text-muted small mb-2">{profile.description}</p>
                                                    )}
                                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                                        {(profile.modules ?? [])
                                                            .filter(m => m.is_active)
                                                            .map(m => (
                                                                <span key={m.id} className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>
                                                                    {m.name}
                                                                </span>
                                                            ))}
                                                        {(profile.modules ?? []).filter(m => m.is_active).length === 0 && (
                                                            <span className="text-muted small">
                                                                <i className="fa fa-info-circle me-1"></i>
                                                                Sin módulos (acceso total)
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Link
                                                            to={`/admin/profiles/${profile.id}/edit`}
                                                            className="btn btn-sm btn-outline-warning flex-fill"
                                                        >
                                                            <i className="fa fa-edit me-1"></i> Editar
                                                        </Link>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => requestDelete(profile)}
                                                            disabled={deleting === profile.id}
                                                            aria-label="Eliminar"
                                                        >
                                                            {deleting === profile.id
                                                                ? <Spinner size="sm" />
                                                                : <i className="fa fa-trash"></i>}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                show={pendingDelete !== null}
                title="Eliminar perfil"
                body={pendingDelete ? `¿Eliminar perfil "${pendingDelete.name}"? Los usuarios asociados quedarán sin perfil.` : ''}
                confirmLabel="Eliminar"
                confirmVariant="danger"
                loading={deleting !== null}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
        </>
    );
};

export default ProfileListPage;
